import Box from '@mui/material/Box';
import React from 'react';

// Kurumsal font politikasi geregi monospace aileleri kullanilamiyor.
// Deger kolonundaki sayilarin hizasi fontVariantNumeric: 'tabular-nums' ile korunuyor.
const FONT_NUM = 'Roboto, "Segoe UI", Arial, sans-serif';
const COLOR_SUCCESS = '#1E8E5A';
const COLOR_ERROR = '#C6362E';
const COLOR_MUTED = '#8A97A2';
const COLOR_BORDER = '#DCE2E7';
const COLOR_KEY_BG = '#EEF1F4';
const COLOR_KEY_TEXT = '#5C6B78';
const COLOR_TEXT = '#1F2A33';
const COLOR_ACCENT = '#0B7285';
const COLOR_CARD_BG = '#FBFCFD';

// Aday listesi tablolarindaki hucre metni - boolean ve bos degerler kullanici diliyle yazilir
function cellText(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Evet' : 'Hayır';
  return String(value);
}

// Aday listeleri kart olarak basilir; dar cekmecede tablo kolonlari birbirini
// eziyordu. Duzen sabit: ilk alan baslik (model kodu), 'Aciklama' varsa alt
// baslik, kalan alanlar (stok, fiyat) etiketli metrik olarak alta dizilir.
const DESCRIPTION_FIELD = 'Aciklama';

function CandidateCard({ item, index, showIndex, titleField, metricFields, hasDescription, labelOf }) {
  return (
    <Box sx={{
      border: `1px solid ${COLOR_BORDER}`,
      borderLeft: `3px solid ${COLOR_ACCENT}`,
      borderRadius: '5px',
      background: COLOR_CARD_BG,
      p: '8px 10px'
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '7px' }}>
        {showIndex && (
          <Box sx={{
            flex: '0 0 auto',
            minWidth: 16,
            height: 16,
            borderRadius: '3px',
            background: COLOR_KEY_BG,
            color: COLOR_KEY_TEXT,
            fontSize: 10,
            fontWeight: 700,
            lineHeight: '16px',
            textAlign: 'center',
            px: '3px'
          }}
          >
            {index + 1}
          </Box>
        )}
        <Box sx={{ fontFamily: FONT_NUM, fontVariantNumeric: 'tabular-nums', fontSize: 12.5, fontWeight: 600, color: COLOR_TEXT, wordBreak: 'break-word' }}>
          {cellText(item[titleField])}
        </Box>
      </Box>
      {hasDescription && (
        <Box sx={{ fontSize: 11.5, color: COLOR_MUTED, mt: '3px', lineHeight: 1.35 }}>
          {cellText(item[DESCRIPTION_FIELD])}
        </Box>
      )}
      {metricFields.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', mt: '7px' }}>
          {metricFields.map((field) => (
            <Box key={field} sx={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
              <Box component="span" sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: COLOR_MUTED }}>
                {labelOf(field)}
              </Box>
              <Box component="span" sx={{ fontSize: 12, fontFamily: FONT_NUM, fontVariantNumeric: 'tabular-nums', color: COLOR_KEY_TEXT, whiteSpace: 'nowrap' }}>
                {cellText(item[field])}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function renderValue(value, resolver) {
  if (value === null || value === undefined) {
    return <span style={{ color: COLOR_MUTED }}>Bilgi yok</span>;
  }
  if (value === '') {
    return <span style={{ color: COLOR_MUTED }}>Boş</span>;
  }
  if (typeof value === 'boolean') {
    return <span style={{ color: value ? COLOR_SUCCESS : COLOR_ERROR, fontWeight: 600 }}>{value ? 'Evet' : 'Hayır'}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span style={{ color: COLOR_MUTED }}>Kayıt yok</span>;
    }
    if (typeof value[0] === 'object' && value[0] !== null) {
      const fields = Object.keys(value[0]);
      const labelOf = (col) => (resolver && resolver.columnLabel ? resolver.columnLabel(col) : col);
      const titleField = fields[0];
      const hasDescription = fields.indexOf(DESCRIPTION_FIELD) >= 0;
      const metricFields = fields.filter((f) => f !== titleField && f !== DESCRIPTION_FIELD);
      return (
        <Box sx={{ display: 'grid', gap: '6px' }}>
          {value.map((item, index) => (
            <CandidateCard
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              item={item}
              index={index}
              showIndex={value.length > 1}
              titleField={titleField}
              metricFields={metricFields}
              hasDescription={hasDescription}
              labelOf={labelOf}
            />
          ))}
        </Box>
      );
    }
    return <span>{value.join(', ')}</span>;
  }
  if (typeof value === 'object') {
    return <JsonKeyValueViewer data={value} />;
  }
  return <span>{String(value)}</span>;
}

// resolver (opsiyonel): sozluk tablosundan gelen etiket/deger cevirisini uygular.
//   label(key)        -> gosterilecek alan adi
//   displayValue(key, value) -> gosterilecek deger (kodlu degerlerin karsiligi)
//   hidden(key)       -> true ise satir hic basilmaz
//   order(key)        -> siralama anahtari
//   columnLabel(col)  -> aday listesi tablosundaki kolon basligi
function JsonKeyValueViewer({ data, json, resolver }) {
  let parsed = data;
  if (!parsed && json) {
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      return <Box sx={{ color: COLOR_ERROR, fontSize: 13 }}>JSON ayrıştırılamadı</Box>;
    }
  }
  if (!parsed || typeof parsed !== 'object' || Object.keys(parsed).length === 0) {
    return <Box sx={{ color: COLOR_MUTED, fontSize: 13 }}>Veri yok</Box>;
  }

  let entries = Object.entries(parsed);
  if (resolver) {
    if (resolver.hidden) {
      entries = entries.filter(([key]) => !resolver.hidden(key));
    }
    if (resolver.order) {
      entries = entries.slice().sort((a, b) => resolver.order(a[0]) - resolver.order(b[0]));
    }
  }
  if (entries.length === 0) {
    return <Box sx={{ color: COLOR_MUTED, fontSize: 13 }}>Veri yok</Box>;
  }

  return (
    <Box sx={{ border: `1px solid ${COLOR_BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
      {entries.map(([key, rawValue], index) => {
        const label = resolver && resolver.label ? resolver.label(key) : key;
        const value = resolver && resolver.displayValue ? resolver.displayValue(key, rawValue) : rawValue;
        return (
          <Box
            key={key}
            sx={{
              display: 'grid',
              // Etiket kolonu okunacak kadar genis kalsin ama deger kolonunu bogmasin
              gridTemplateColumns: 'minmax(120px, 36%) 1fr',
              borderTop: index === 0 ? 'none' : `1px solid ${COLOR_BORDER}`
            }}
          >
            <Box sx={{ p: '7px 10px', background: COLOR_KEY_BG, color: COLOR_KEY_TEXT, fontSize: 12, borderRight: `1px solid ${COLOR_BORDER}`, wordBreak: 'break-word' }}>
              {label}
            </Box>
            <Box sx={{ p: '7px 10px', fontFamily: FONT_NUM, fontVariantNumeric: 'tabular-nums', fontSize: 12, wordBreak: 'break-word' }}>
              {renderValue(value, resolver)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default JsonKeyValueViewer;
